import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, Check, CheckCircle2, ExternalLink, Plus, RefreshCw, TriangleAlert, Trash2, Wand2, X } from '@/components/icons';
import { DRAFT_KIND_LABEL, DRAFT_KINDS, STEP_KINDS, type DraftKind, type Requirement, type RequirementSetView, type StepKind, type TestDraft } from '@1qa/shared';
import { draftBlocked, missingDataOf, newStepId, PRIORITY_LABEL, PRIORITY_TONE } from '@/lib/requirements';
import { Badge, Button, Card, CardHeader, Input, Select, Textarea } from '@/components/ui';

const STEP_LABEL: Record<StepKind, string> = { open: 'Open', act: 'Do', assert: 'Check', wait: 'Wait' };
const KIND_TONE: Record<DraftKind, 'success' | 'danger' | 'info'> = { positive: 'success', negative: 'danger', edge: 'info' };

function DraftCard({
  draft,
  canEdit,
  web,
  projectId,
  blocked,
  onChange,
}: {
  draft: TestDraft;
  canEdit: boolean;
  web: boolean;
  projectId: string;
  blocked: string[];
  onChange: (d: TestDraft) => void;
}) {
  const [open, setOpen] = useState(false);
  const saved = !!draft.savedTestId;
  const editable = canEdit && !saved;
  const setStep = (i: number, patch: Partial<TestDraft['steps'][number]>) => onChange({ ...draft, steps: draft.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i: number, by: number) => {
    const steps = [...draft.steps];
    const [s] = steps.splice(i, 1);
    steps.splice(i + by, 0, s!);
    onChange({ ...draft, steps });
  };

  return (
    <li
      className={clsx(
        'rounded-card ring-1 ring-inset transition',
        draft.status === 'accepted' || saved ? 'bg-surface ring-success/50' : draft.status === 'rejected' ? 'bg-surface-sunken opacity-60 ring-border' : 'bg-surface ring-border',
      )}
      data-testid={`draft-${draft.id}`}
    >
      <div className="flex flex-wrap items-start gap-3 p-4">
        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{draft.name}</span>
            <Badge tone={KIND_TONE[draft.kind]}>{DRAFT_KIND_LABEL[draft.kind]}</Badge>
            <Badge tone={PRIORITY_TONE[draft.priority]}>{PRIORITY_LABEL[draft.priority]}</Badge>
            {draft.requirementKeys.map((k) => (
              <span key={k} className="font-mono text-[11px] text-fg-muted">
                {k}
              </span>
            ))}
          </span>
          {draft.description && <span className="mt-1 line-clamp-2 block text-xs text-fg-secondary">{draft.description}</span>}
          <span className="mt-1 block text-xs text-fg-muted">
            {draft.steps.length} steps · {open ? 'hide' : 'show'} steps
          </span>
        </button>
        {saved ? (
          <Link to={`/projects/${projectId}/tests/${draft.savedTestId}`} className="inline-flex items-center gap-1.5 text-xs font-medium text-success hover:underline">
            <CheckCircle2 className="size-3.5" /> Saved · open test
          </Link>
        ) : (
          canEdit && (
            <span className="flex gap-2">
              <Button
                size="sm"
                variant={draft.status === 'accepted' ? 'primary' : 'secondary'}
                icon={<Check className="size-3.5" />}
                onClick={() => onChange({ ...draft, status: draft.status === 'accepted' ? 'draft' : 'accepted' })}
                aria-pressed={draft.status === 'accepted'}
              >
                {draft.status === 'accepted' ? 'Accepted' : 'Accept'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon={<X className="size-3.5" />}
                onClick={() => onChange({ ...draft, status: draft.status === 'rejected' ? 'draft' : 'rejected' })}
                aria-pressed={draft.status === 'rejected'}
              >
                {draft.status === 'rejected' ? 'Rejected' : 'Reject'}
              </Button>
            </span>
          )
        )}
      </div>
      {blocked.length > 0 && !saved && draft.status !== 'rejected' && (
        <p className="mx-4 mb-3 flex items-start gap-2 rounded-field bg-warning-subtle px-3 py-2 text-xs text-fg">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden />
          Needs test data before it can be saved: {blocked.map((n) => `{{${n}}}`).join(', ')}
        </p>
      )}
      {open && (
        <div className="space-y-3 border-t border-border-subtle p-4">
          {editable && (
            <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
              <Input aria-label="Test name" value={draft.name} maxLength={200} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
              <Select aria-label="Kind" value={draft.kind} onChange={(e) => onChange({ ...draft, kind: e.target.value as DraftKind })} options={DRAFT_KINDS.map((k) => ({ value: k, label: DRAFT_KIND_LABEL[k] }))} />
            </div>
          )}
          {editable && <Textarea aria-label="Description" rows={2} value={draft.description} maxLength={4000} onChange={(e) => onChange({ ...draft, description: e.target.value })} />}
          <ol className="space-y-2">
            {draft.steps.map((s, i) => (
              <li key={s.id} className="flex items-start gap-2">
                <span className="mt-2.5 w-5 shrink-0 text-right text-xs text-fg-muted tabular-nums">{i + 1}</span>
                {editable ? (
                  <>
                    <Select
                      aria-label={`Step ${i + 1} kind`}
                      wrapperClassName="w-28 shrink-0"
                      value={s.kind}
                      onChange={(e) => setStep(i, { kind: e.target.value as StepKind, ...(e.target.value === 'open' && !web ? { value: '' } : {}) })}
                      options={STEP_KINDS.map((k) => ({ value: k, label: STEP_LABEL[k] }))}
                    />
                    <Input
                      aria-label={`Step ${i + 1}`}
                      className="min-w-0 flex-1"
                      value={s.value}
                      disabled={s.kind === 'open' && !web}
                      placeholder={s.kind === 'open' ? (web ? '/' : 'Launches the app') : s.kind === 'wait' ? 'Milliseconds' : ''}
                      onChange={(e) => setStep(i, { value: e.target.value })}
                    />
                    <span className="flex shrink-0 gap-1 pt-2">
                      <button type="button" aria-label="Move up" disabled={i === 0} className="text-fg-muted hover:text-fg disabled:opacity-30" onClick={() => move(i, -1)}>
                        <ArrowUp className="size-3.5" />
                      </button>
                      <button type="button" aria-label="Move down" disabled={i === draft.steps.length - 1} className="text-fg-muted hover:text-fg disabled:opacity-30" onClick={() => move(i, 1)}>
                        <ArrowDown className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label="Remove step"
                        disabled={draft.steps.length === 1}
                        className="text-fg-muted hover:text-danger-text disabled:opacity-30"
                        onClick={() => onChange({ ...draft, steps: draft.steps.filter((_, j) => j !== i) })}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </span>
                  </>
                ) : (
                  <p className="pt-2 text-sm text-fg">
                    <span className="mr-2 text-xs font-semibold tracking-label text-fg-muted uppercase">{STEP_LABEL[s.kind]}</span>
                    {s.kind === 'open' && !web ? 'Launch the app' : s.value}
                  </p>
                )}
              </li>
            ))}
          </ol>
          {editable && (
            <Button variant="ghost" size="sm" icon={<Plus className="size-3.5" />} onClick={() => onChange({ ...draft, steps: [...draft.steps, { id: newStepId(), kind: 'assert', value: '' }] })}>
              Add step
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/** Step 3: the drafted tests by requirement, with coverage and the test data they still need. */
export function DraftsStep({
  set,
  requirements,
  drafts,
  web,
  onChange,
  onGenerate,
  onRegenerate,
  onNext,
  generating,
  coverage,
}: {
  set: RequirementSetView;
  requirements: Requirement[];
  drafts: TestDraft[];
  web: boolean;
  onChange: (next: TestDraft[]) => void;
  onGenerate: () => void;
  onRegenerate: (key: string) => void;
  onNext: () => void;
  generating: boolean;
  coverage: Map<string, { drafts: number; accepted: number; saved: number }>;
}) {
  const [focus, setFocus] = useState<string | null>(null);
  const [kind, setKind] = useState<'' | DraftKind>('');
  const canEdit = set.canEdit;
  const missing = useMemo(() => missingDataOf(drafts, set.dataKeys), [drafts, set.dataKeys]);
  const reqByKey = new Map(requirements.map((r) => [r.key, r]));
  const groups = requirements
    .filter((r) => !focus || r.key === focus)
    .map((r) => ({ r, items: drafts.filter((d) => d.requirementKeys[0] === r.key && (!kind || d.kind === kind)) }))
    .filter((g) => g.items.length || g.r.key === focus);
  const uncovered = requirements.filter((r) => r.selected && !(coverage.get(r.key)?.drafts ?? 0));
  const accepted = drafts.filter((d) => d.status === 'accepted' && !d.savedTestId).length;
  const update = (d: TestDraft) => onChange(drafts.map((x) => (x.id === d.id ? d : x)));
  const setAll = (status: TestDraft['status']) =>
    onChange(drafts.map((d) => (d.savedTestId || (kind && d.kind !== kind) || (focus && !d.requirementKeys.includes(focus)) ? d : { ...d, status })));

  return (
    <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
      <Card className="h-fit xl:sticky xl:top-4">
        <CardHeader title="Coverage" description="Tests per requirement (accepted / drafted)." />
        <ul className="max-h-[60vh] overflow-auto py-2">
          <li>
            <button type="button" onClick={() => setFocus(null)} className={clsx('w-full px-5 py-2 text-left text-sm', !focus ? 'font-semibold text-fg' : 'text-fg-secondary hover:text-fg')}>
              All requirements
            </button>
          </li>
          {requirements.map((r) => {
            const c = coverage.get(r.key) ?? { drafts: 0, accepted: 0, saved: 0 };
            return (
              <li key={r.key}>
                <button
                  type="button"
                  onClick={() => setFocus(focus === r.key ? null : r.key)}
                  className={clsx('flex w-full items-center gap-2 px-5 py-2 text-left text-sm', focus === r.key ? 'bg-primary-subtle/50 text-fg' : 'text-fg-secondary hover:text-fg')}
                >
                  <span
                    className={clsx('size-2 shrink-0 rounded-full', c.accepted ? 'bg-success' : c.drafts ? 'bg-warning' : r.selected ? 'bg-danger' : 'bg-border')}
                    aria-hidden
                  />
                  <span className="font-mono text-[11px] text-fg-muted">{r.key}</span>
                  <span className="min-w-0 flex-1 truncate">{r.title}</span>
                  <span className="shrink-0 text-xs tabular-nums">
                    {c.accepted}/{c.drafts}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {canEdit && uncovered.length > 0 && (
          <div className="border-t border-border-subtle px-5 py-3">
            <Button variant="secondary" size="sm" className="w-full" icon={<Wand2 className="size-3.5" />} loading={generating} onClick={onGenerate}>
              Write tests for {uncovered.length} uncovered
            </Button>
          </div>
        )}
      </Card>

      <div className="space-y-6">
        {missing.length > 0 && (
          <Card className="p-5" data-testid="drafts-missing-data">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
                  <TriangleAlert className="size-4 text-warning" aria-hidden /> Test data the tests use
                </h3>
                <p className="mt-1 text-xs text-fg-secondary">
                  Add these names on the Test data page (passwords as secret). Names with an example value work without it; the others block saving.
                </p>
              </div>
              <Link to={`/projects/${set.projectId}?tab=data`} target="_blank" className="inline-flex items-center gap-1.5 text-xs font-medium text-link hover:underline">
                Open Test data <ExternalLink className="size-3" />
              </Link>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {missing.map((m) => (
                <span
                  key={m.name}
                  title={[...m.blocking, ...m.written].join(', ')}
                  className={clsx('rounded-full px-2.5 py-1 font-mono text-xs', m.blocking.length ? 'bg-warning-subtle text-fg' : 'bg-surface-raised text-fg-secondary')}
                >
                  {`{{${m.name}}}`} · {m.blocking.length + m.written.length} {m.blocking.length + m.written.length === 1 ? 'test' : 'tests'}
                  {!m.blocking.length && ' · has example'}
                </span>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <CardHeader
            title={`3. Test cases (${drafts.filter((d) => d.status !== 'rejected').length})`}
            description="Read each test, fix the steps if needed, then accept or reject it. Only accepted tests are saved."
            actions={
              <Select
                aria-label="Kind"
                wrapperClassName="w-40"
                value={kind}
                onChange={(e) => setKind(e.target.value as '' | DraftKind)}
                options={[{ value: '', label: 'All kinds' }, ...DRAFT_KINDS.map((k) => ({ value: k, label: DRAFT_KIND_LABEL[k] }))]}
              />
            }
          />
          {canEdit && drafts.length > 0 && (
            <div className="flex flex-wrap gap-3 border-b border-border-subtle px-5 py-3 text-xs">
              <button type="button" className="text-link hover:underline" onClick={() => setAll('accepted')}>
                Accept all shown
              </button>
              <button type="button" className="text-link hover:underline" onClick={() => setAll('draft')}>
                Clear choices
              </button>
            </div>
          )}
          <div className="space-y-6 p-5">
            {groups.map(({ r, items }) => (
              <section key={r.key} aria-label={`${r.key} ${r.title}`}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-fg">
                    <span className="mr-2 font-mono text-xs text-fg-muted">{r.key}</span>
                    {r.title}
                  </h3>
                  {canEdit && (
                    <Button variant="ghost" size="sm" icon={<RefreshCw className="size-3.5" />} disabled={generating} onClick={() => onRegenerate(r.key)}>
                      {items.length ? 'Write again' : 'Write tests'}
                    </Button>
                  )}
                </div>
                {items.length ? (
                  <ul className="space-y-3">
                    {items.map((d) => (
                      <DraftCard key={d.id} draft={d} canEdit={canEdit} web={web} projectId={set.projectId} blocked={draftBlocked(d, set.dataKeys)} onChange={update} />
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-fg-muted">No test cases for this requirement yet.</p>
                )}
                {items.some((d) => d.requirementKeys.length > 1) && (
                  <p className="mt-2 text-xs text-fg-muted">
                    Some tests also cover {[...new Set(items.flatMap((d) => d.requirementKeys.slice(1)))].map((k) => `${k} ${reqByKey.get(k)?.title ?? ''}`.trim()).join(', ')}.
                  </p>
                )}
              </section>
            ))}
            {!groups.length && <p className="py-8 text-center text-sm text-fg-muted">No test cases match.</p>}
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-5 py-4">
              <span className="text-sm text-fg-secondary">
                {accepted} accepted{set.counts.saved > 0 && ` · ${set.counts.saved} saved`}
              </span>
              <Button disabled={!accepted} onClick={onNext} data-testid="drafts-next">
                Review and save {accepted} {accepted === 1 ? 'test' : 'tests'}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
